import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";

// Pages
import LandingPage from "./pages/LandingPage";
import VolunteerApplication from "./pages/VolunteerApplication";
import VolunteerRoles from "./pages/VolunteerRoles";
import VolunteerPortal from "./pages/VolunteerPortal";
import ApplicationConfirmation from "./pages/ApplicationConfirmation";
import AdminLogin from "./pages/AdminLogin";
import AuthCallback from "./pages/AuthCallback";
import Dashboard from "./pages/admin/Dashboard";
import VolunteerDatabase from "./pages/admin/VolunteerDatabase";
import VolunteerDetail from "./pages/admin/VolunteerDetail";
import TaskManagement from "./pages/admin/TaskManagement";
import EventManagement from "./pages/admin/EventManagement";
import HourTracking from "./pages/admin/HourTracking";
import BackgroundChecks from "./pages/admin/BackgroundChecks";
import ImpactDashboard from "./pages/admin/ImpactDashboard";
import Settings from "./pages/admin/Settings";
import AdminManagement from "./pages/admin/AdminManagement";

// Corporate Pages
import CorporateLanding from "./pages/CorporateLanding";
import CorporateInterestForm from "./pages/CorporateInterestForm";
import CorporateConfirmation from "./pages/CorporateConfirmation";
import CorporateEmployeeRegistration from "./pages/CorporateEmployeeRegistration";
import CorporateToolkit from "./pages/CorporateToolkit";
import CorporateDashboard from "./pages/admin/CorporateDashboard";
import CorporateEventDetail from "./pages/admin/CorporateEventDetail";
import CorporateOutreach from "./pages/admin/CorporateOutreach";

// Donation Pages
import DonationForm from "./pages/DonationForm";
import DonationManagement from "./pages/admin/DonationManagement";

// Document Management
import DocumentManagement from "./pages/admin/DocumentManagement";

import "./App.css";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#5cc8e8] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }

  return children;
};

// App Router with session_id detection
function AppRouter() {
  const location = useLocation();

  // CRITICAL: Check URL fragment for session_id synchronously during render
  // This prevents race conditions by processing new session_id FIRST
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/volunteer-roles" element={<VolunteerRoles />} />
      <Route path="/volunteer-portal" element={<VolunteerPortal />} />
      <Route path="/apply" element={<VolunteerApplication />} />
      <Route path="/application-confirmation" element={<ApplicationConfirmation />} />
      <Route path="/admin/login" element={<AdminLogin />} />

      {/* Corporate Public Routes */}
      <Route path="/corporate" element={<CorporateLanding />} />
      <Route path="/corporate/request" element={<CorporateInterestForm />} />
      <Route path="/corporate/confirmation" element={<CorporateConfirmation />} />
      <Route path="/corporate/register/:token" element={<CorporateEmployeeRegistration />} />
      <Route path="/corporate/toolkit" element={<CorporateToolkit />} />

      {/* Donation Public Route */}
      <Route path="/donate" element={<DonationForm />} />

      {/* Protected Admin Routes */}
      <Route path="/admin" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/admin/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/admin/volunteers" element={<ProtectedRoute><VolunteerDatabase /></ProtectedRoute>} />
      <Route path="/admin/volunteers/:id" element={<ProtectedRoute><VolunteerDetail /></ProtectedRoute>} />
      <Route path="/admin/tasks" element={<ProtectedRoute><TaskManagement /></ProtectedRoute>} />
      <Route path="/admin/events" element={<ProtectedRoute><EventManagement /></ProtectedRoute>} />
      <Route path="/admin/hours" element={<ProtectedRoute><HourTracking /></ProtectedRoute>} />
      <Route path="/admin/background-checks" element={<ProtectedRoute><BackgroundChecks /></ProtectedRoute>} />
      <Route path="/admin/impact" element={<ProtectedRoute><ImpactDashboard /></ProtectedRoute>} />
      
      {/* Corporate Admin Routes */}
      <Route path="/admin/corporate" element={<ProtectedRoute><CorporateDashboard /></ProtectedRoute>} />
      <Route path="/admin/corporate/outreach" element={<ProtectedRoute><CorporateOutreach /></ProtectedRoute>} />
      <Route path="/admin/corporate/events/:eventId" element={<ProtectedRoute><CorporateEventDetail /></ProtectedRoute>} />
      <Route path="/admin/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="/admin/admins" element={<ProtectedRoute><AdminManagement /></ProtectedRoute>} />

      {/* Donation Admin Route */}
      <Route path="/admin/donations" element={<ProtectedRoute><DonationManagement /></ProtectedRoute>} />

      {/* Document Admin Route */}
      <Route path="/admin/documents" element={<ProtectedRoute><DocumentManagement /></ProtectedRoute>} />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
